import { errorCode, httpStatus } from "@utils/errors";
import createAppQueryCache from "@Core/queries/createAppQueryCache";
import { hasSilentErrors } from "@Core/queries/silentErrors";
import { toast } from "@UI/components/Molecules/Notification/toast.ts";
import { QueryClient, MutationCache } from "@tanstack/react-query";
import { trackLogRocket } from "@services/logRocket";
import { ValidationError } from "yup";
import { AxiosError } from "axios";
import {
  ALLOWEAT_API_ERROR,
  ALLOWEAT_API_SERVER_ERROR,
} from "@utils/logRocket";
import { ENVIRONMENT } from "@config/app";
import UnexpectedErrorMessage from "@Core/components/UnexpectedErrorMessage/UnexpectedErrorMessage";

const trackApiError = (status: number | undefined) => {
  if (ENVIRONMENT !== "prod" || status === undefined || status < 400) return;

  trackLogRocket(
    status >= 500 ? ALLOWEAT_API_SERVER_ERROR : ALLOWEAT_API_ERROR,
  );
};

const queryCache = createAppQueryCache(trackApiError);

const mutationCache = new MutationCache({
  onError: (e: unknown, _variables, _context, mutation) => {
    if (hasSilentErrors(mutation.meta)) return;

    console.error(e);

    const status = httpStatus(e);

    if (status === 403) {
      window.dispatchEvent(new Event("app:blocked"));
      return;
    }

    trackApiError(status);

    if (e === "Already confirmed") {
      return;
    }

    if (mutation.options.onError) return;
    toast.error(<UnexpectedErrorMessage />);
  },
});

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: unknown) => {
        if (errorCode(error) === AxiosError.ERR_NETWORK) return false;

        if (error instanceof ValidationError) {
          return false;
        }

        const code = errorCode(error);
        if (typeof code === "number" && code >= 400) return false;

        const status = httpStatus(error);
        if (status !== undefined && status >= 400) return false;

        return failureCount < 3;
      },
    },
  },
});
